import { useEffect, useState } from "react";
import api from "@/lib/api";
import { ChevronDown, ChevronRight, Sparkles, MessageSquare, CornerDownRight, ExternalLink } from "lucide-react";
import { AiSuggestedBadge } from "@/components/AiSuggestedBadge";

const KIND_META = {
  ask: { icon: MessageSquare, label: "Asked AI", color: "text-slate-500" },
  return: { icon: Sparkles, label: "AI returned", color: "text-violet-600" },
  decision: { icon: CornerDownRight, label: "Human decision", color: "text-emerald-600" },
};

// Quiet, collapsible "AI research history (N)" — append-only ask/return/decision events.
// Reads only; available when you look, not cluttering the main view.
export function AiResearchHistory({ partId }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!partId) return;
    api.get(`/parts/${partId}/ai-research`).then(({ data }) => { setEvents(data.events || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, [partId]);

  if (loaded && events.length === 0) return null;   // nothing to show, stay out of the way

  return (
    <div data-testid="ai-history-section" className="mt-8 border-t border-slate-100 pt-4">
      <button data-testid="ai-history-toggle" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-600 transition-colors">
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Sparkles className="w-3.5 h-3.5 text-violet-400" />
        AI research history ({events.length})
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {events.map((e) => {
            const m = KIND_META[e.kind] || KIND_META.ask;
            const Icon = m.icon;
            return (
              <div key={e.id} data-testid={`ai-history-entry-${e.id}`} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 text-xs">
                <div className={`flex items-center gap-1.5 font-medium ${m.color}`}>
                  <Icon className="w-3.5 h-3.5" />{m.label}
                  <span className="text-slate-400 font-normal">· {e.by || e.model || ""} · {(e.at || "").replace("T", " ").slice(0, 16)}</span>
                </div>
                {e.kind === "decision" && (
                  <div data-testid={`ai-history-decision-${e.id}`} className="mt-1 text-slate-600">
                    <span className="font-semibold capitalize">{(e.conclusion || "").replace("_", " ")}</span>
                    {e.entered_value ? <> → entered <span className="font-mono">{e.entered_value}</span></> : null}
                    {e.note ? <div className="text-slate-500 italic mt-0.5">"{e.note}"</div> : null}
                  </div>
                )}
                {e.kind === "ask" && (
                  <div className="mt-1 text-slate-500">
                    {(e.intents || []).length ? <>asked: {(e.intents || []).join(", ")}</> : null}
                    {(e.photo_file_ids || []).length ? <> · {e.photo_file_ids.length} photo(s)</> : null}
                  </div>
                )}
                {e.kind === "return" && (
                  <div className="mt-1 space-y-1">
                    {(e.suggestions || []).map((s, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-1.5 text-slate-600">
                        <span className="text-slate-400">{s.bucket}:</span>
                        <span className="font-medium">{String(s.value)}</span>
                        <AiSuggestedBadge />
                        {(s.source_links || []).map((l, j) => (
                          <a key={j} href={l} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center gap-0.5 underline"><ExternalLink className="w-3 h-3" />source</a>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
